import React, { useState } from 'react';
import { ASSETS } from '../utils/assets';

interface CookiePreferences {
  necessary: boolean;
  analytics: boolean;
  version: number;
}

interface CookieDialogProps {
  isVisible: boolean;
  onClose: () => void;
}

const CookieDialog: React.FC<CookieDialogProps> = ({ isVisible, onClose }) => {
  const [analyticsEnabled, setAnalyticsEnabled] = useState(false);

  // Cookie-Hilfsfunktionen
  const setCookie = (name: string, value: string, days: number) => {
    const date = new Date();
    date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
    const expires = "expires=" + date.toUTCString();
    document.cookie = `${name}=${value};${expires};path=/;SameSite=Strict`;
  };

  // Cookie-Präferenzen speichern
  const savePreferences = (preferences: CookiePreferences) => {
    setCookie("cookieConsent", JSON.stringify(preferences), 365);
    applyConsentPreferences(preferences);
    onClose();
  };

  // Analytics aktivieren/deaktivieren
  const enableAnalytics = () => {
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if ((window as any).amplitude) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (window as any).amplitude.setOptOut(false);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (window as any).amplitude.getInstance().logEvent('Cookie_Analytics_Accepted');
        console.log("Amplitude Analytics aktiviert");
      }
    } catch (e) {
      console.error("Fehler beim Aktivieren von Analytics:", e);
    }
  };

  const disableAnalytics = () => {
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if ((window as any).amplitude) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (window as any).amplitude.setOptOut(true);
        console.log("Amplitude Analytics deaktiviert");
      }
    } catch (e) {
      console.error("Fehler beim Deaktivieren von Analytics:", e);
    }
  };

  const applyConsentPreferences = (preferences: CookiePreferences) => {
    if (preferences.analytics) {
      enableAnalytics();
    } else {
      disableAnalytics();
    }
  };

  // Event Handler
  const handleAcceptAll = () => {
    const preferences: CookiePreferences = {
      necessary: true,
      analytics: true,
      version: 1
    };
    savePreferences(preferences);
  };

  const handleAcceptSelected = () => {
    const preferences: CookiePreferences = {
      necessary: true,
      analytics: analyticsEnabled,
      version: 1
    };
    savePreferences(preferences);
  };

  const handleDeclineAll = () => {
    const preferences: CookiePreferences = {
      necessary: true,
      analytics: false,
      version: 1
    };
    savePreferences(preferences);
  };

  if (!isVisible) return null;

  return (
    <>
      {/* Overlay */}
      <div id="cookie-overlay" className="cookie-overlay"></div>

      {/* Cookie Dialog */}
      <div id="cookie-dialog" className="cookie-dialog">
        <div className="cookie-content">
          <div className="cookie-header">
            <img src={ASSETS.cookieIcon} alt="Cookie Icon" />
            <h3>Cookie-Einstellungen</h3>
          </div>

          <div className="cookie-body">
            <p>
              Wir verwenden Cookies, um Ihre Erfahrung auf unserer Website zu verbessern 
              und anonyme Nutzungsstatistiken zu erstellen. Sie können Ihre Einstellungen 
              jederzeit anpassen.
            </p>

            <div className="cookie-categories">
              {/* Notwendige Cookies */}
              <div className="cookie-category">
                <div className="cookie-category-header">
                  <label className="cookie-toggle">
                    <input
                      type="checkbox"
                      checked={true}
                      disabled={true}
                      readOnly
                    />
                    <span className="cookie-slider disabled"></span>
                  </label>
                  <div>
                    <h4>Notwendige Cookies</h4>
                    <p>
                      Diese Cookies sind für die Grundfunktionen der Website erforderlich 
                      und können nicht deaktiviert werden.
                    </p>
                  </div>
                </div>
              </div>

              {/* Analytics Cookies */}
              <div className="cookie-category">
                <div className="cookie-category-header">
                  <label className="cookie-toggle">
                    <input
                      type="checkbox"
                      id="analytics-cookies"
                      checked={analyticsEnabled}
                      onChange={(e) => setAnalyticsEnabled(e.target.checked)}
                    />
                    <span className="cookie-slider"></span>
                  </label>
                  <div>
                    <h4>Analytics-Cookies</h4>
                    <p>
                      Diese Cookies helfen uns zu verstehen, wie Besucher mit unserer 
                      Website interagieren, indem sie anonyme Informationen sammeln.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="cookie-footer">
            <div className="cookie-buttons">
              <button
                id="decline-all"
                className="cookie-btn secondary"
                onClick={handleDeclineAll}
              >
                Alle ablehnen
              </button>
              <button
                id="accept-selected"
                className="cookie-btn secondary"
                onClick={handleAcceptSelected}
              >
                Auswahl akzeptieren
              </button>
              <button
                id="accept-all"
                className="cookie-btn primary"
                onClick={handleAcceptAll}
              >
                Alle akzeptieren
              </button>
            </div>

            <div className="cookie-links">
              <a href="/privacy.html" target="_blank" rel="noopener noreferrer">
                Datenschutzerklärung
              </a>
              <a href="/impressum.html" target="_blank" rel="noopener noreferrer">
                Impressum
              </a>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CookieDialog;
